'use client';

import React, { useMemo } from 'react';
import type { PermissionIdsType } from '@/consts/permissions';
import { usePermissions } from '@/context/PermissionsProvider';
import { Tooltip } from '../Tooltip';
import { TooltipWrapper } from '../Tooltip/components/TooltipWrapper';
import { tabHighlightStyles, tabIconStyles, tabStyles, tabsContainerStyles } from './styles';
import type { TabsIntentType } from './types';

export interface TabItem {
  id: string | number;
  label: string;
  disabled?: boolean;
  intent?: TabsIntentType;
  permission?: PermissionIdsType | PermissionIdsType[];
  tooltip?: string;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
}

export interface TabsProps<T> {
  tabs: TabItem[];
  activeTab: T;
  onTabChange: (id: T) => void;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  className?: string;
  isHidden?: boolean;
  intent?: TabsIntentType;
}

interface TabsButtonProps {
  label: string;
  active: boolean;
  tooltip?: string;
  onClick: () => void;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  disabled?: boolean;
  intent?: TabsIntentType;
}

const TabsButton: React.FC<TabsButtonProps> = ({ label, active, onClick, disabled, intent, tooltip, Icon }) => {
  return (
    <Tooltip key={label} isVisible={!!tooltip} content={<TooltipWrapper>{tooltip}</TooltipWrapper>} placement="top">
      <button
        type="button"
        onClick={onClick}
        className={tabStyles({ active, disabled: disabled ?? false, intent })}
        disabled={disabled}
      >
        {Icon && active && (
          <span className="inline-flex items-center justify-center w-6 h-6 mr-1 mb-[2px]">
            {<Icon className={tabIconStyles({ intent })} />}
          </span>
        )}
        {label}
        {active && <div className={tabHighlightStyles({ intent })} />}
      </button>
    </Tooltip>
  );
};

const Tabs = <T,>({ tabs, activeTab, onTabChange, className, isHidden = false, intent = 'default' }: TabsProps<T>) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (!tab.permission) return true;
      return Array.isArray(tab.permission) ? hasAnyPermission(tab.permission) : hasPermission(tab.permission);
    });
  }, [tabs, hasPermission, hasAnyPermission]);

  return (
    <div className={tabsContainerStyles({ className, isHidden, intent })}>
      {visibleTabs.map((tab: TabItem) => (
        <TabsButton
          key={tab.id}
          label={tab.label}
          tooltip={tab.tooltip}
          active={activeTab === tab.id}
          Icon={tab.Icon}
          onClick={() => onTabChange(tab.id as T)}
          disabled={tab.disabled}
          intent={intent}
        />
      ))}
    </div>
  );
};

export default Tabs;